import {LocalizationStrings} from "../../../i18n/i18n-model";
import {FormFieldConfig, translateFormValue} from "../../../tools/form/form-field-config";
import {getMeasurementTypeDisplayLocalizationKey, MeasurementType} from "../../measurement-models";
import {UploadFormModel} from "../../upload/upload-form-model";
import {Analysis, createUploadFormConfig} from "../analysis";
import {reportLogoUrl, ReportValue} from "../report";
import {EcgReportModel} from "./ecg-report-model";

export class EcgReportModelFactory {
  constructor(
    private readonly ecgAnalysis: Analysis,
    private readonly reportDate: string,
    private readonly translate: (key: keyof LocalizationStrings) => string,
    private readonly dateTimeTransform: (date?: string) => string,
    private readonly userToken: string
  ) {
  }

  createEcgReportModel(): EcgReportModel {
    const config = createUploadFormConfig(this.ecgAnalysis);

    const symptomsList: ReportValue[] = this.ecgAnalysis.doctorFindings.symptomsComment === undefined
      ? []
      : this.ecgAnalysis.patientData.symptoms.map((symptom, index) => ({
        label: `${this.dateTimeTransform(symptom.time)}`,
        value: symptom.description || "",
      }));

    const patient = this.ecgAnalysis.usesDateOfBirth
      ? [this.createFormFieldValue(config.gender), this.calculatedAgeAtMeasurementTime()].filter((s) => s!).join(", ")
      : [this.createFormFieldValue(config.gender), this.createFormFieldValue(config.age)].filter((s) => s!).join(", ");

    return {
      uniqueId: this.reportValue("reportUniqueIdLabel", this.ecgAnalysis.uniqueId),
      measurementType: this.ecgAnalysis.type,
      measurementCode: this.reportValue("uploadFormLabelCode", this.ecgAnalysis.code),
      patient: this.reportValue("reportLabelPatient", patient),
      startTime: this.reportValue("analysisLabelStartTime", this.dateTimeTransform(this.ecgAnalysis.startTime)),
      patientStatusComments: this.reportValue("analysisLabelPatientStatusComment", this.ecgAnalysis.doctorFindings.patientStatusComments),
      findings: this.reportValue("analysisLabelFindings", this.ecgAnalysis.doctorFindings.findings),
      conclusion: this.reportValue("analysisLabelConclusion", this.ecgAnalysis.doctorFindings.conclusion),
      diagnosis: this.reportValue("analysisLabelDiagnosis", this.ecgAnalysis.doctorFindings.diagnosis),
      reportDate: this.reportDate,
      doctor: {
        fullName: `${this.ecgAnalysis.doctor.firstName} ${this.ecgAnalysis.doctor.lastName}`.trim(),
        city: this.ecgAnalysis.doctor.city || "",
        title: this.ecgAnalysis.doctor.title || "",
      },
      patientData: {
        gender: this.patientDataReportValue(config.gender),
        ...(this.ecgAnalysis.usesDateOfBirth
          ? {dateOfBirth: this.patientDataReportValue(config.dateOfBirth)}
          : {age: this.patientDataReportValue(config.age)}),
        height: this.patientDataReportValue(config.height),
        weight: this.patientDataReportValue(config.weight),
        overweight: this.patientDataReportValue(config.overweight),
        smoking: this.patientDataReportValue(config.smoking),
        abnormalHeartbeats: this.patientDataReportValue(config.abnormalHeartbeats),
        slowPulse: this.patientDataReportValue(config.slowPulse),
        fastPulse: this.patientDataReportValue(config.fastPulse),
        palpitation: this.patientDataReportValue(config.palpitation),
        irregularPulse: this.patientDataReportValue(config.irregularPulse),
        dizziness: this.patientDataReportValue(config.dizziness),
        lossOfConsciousness: this.patientDataReportValue(config.lossOfConsciousness),
        blackouts: this.patientDataReportValue(config.blackouts),
        chestPain: this.patientDataReportValue(config.chestPain),
        shortnessOfBreath: this.patientDataReportValue(config.shortnessOfBreath),
        diagnosedHeartDisease: this.patientDataReportValue(config.diagnosedHeartDisease),
        currentMedication: this.patientDataReportValue(config.currentMedication),
        reasonForStudy: this.patientDataReportValue(config.reasonForStudy),
        patientFeedback: this.patientDataReportValue(config.patientFeedback),
        diary: this.patientDataReportValue(config.diary),
      },
      patientDataPageTitle: this.translate("reportPatientDataPageTitle"),
      findingsPageTitle: this.translate(getMeasurementTypeDisplayLocalizationKey({
        type: this.ecgAnalysis.type,
        isHome: false,
        actualDurationDays: this.ecgAnalysis.durationDays
      })),
      ecgAtrialFibrillationPageTitle: this.translate("ecgReportAtrialFibrillationTitle"),
      symptomFindingsTitle: this.translate("ecgReportSymptomFindingsTitle"),
      pageLabel: this.translate("reportPageLabel"),
      logoUrlPath: reportLogoUrl(this.ecgAnalysis.id, this.userToken),
      externalAnalysisUrlLabel: this.translate("externalAnalysisUrlLabel"),
      externalAnalysisUrl: this.ecgAnalysis.externalAnalysisUrl,
      symptomsComment: this.ecgAnalysis.doctorFindings.symptomsComment,
      symptomsList: symptomsList
    };
  }

  private reportValue(label: keyof LocalizationStrings | undefined, value: string | undefined): ReportValue {
    return {
      label: label ? this.translate(label) : "",
      value: value || "",
    };
  }

  private patientDataReportValue(formFieldConfig: FormFieldConfig<UploadFormModel> | undefined): ReportValue {
    return this.reportValue(formFieldConfig?.label, this.createFormFieldValue(formFieldConfig));
  }

  private calculatedAgeAtMeasurementTime(): string {
    const calculatedAge = this.ecgAnalysis.patientData.calculatedAge;
    return `${calculatedAge} ${this.translate("uploadFormInfoLabelAge")}`;
  }

  private createFormFieldValue(formFieldConfig: FormFieldConfig<UploadFormModel> | undefined): string {
    if (formFieldConfig === undefined) {
      return "";
    }

    const formValue = translateFormValue(formFieldConfig, formFieldConfig.model[formFieldConfig.property], (key) =>
      this.translate(key));
    const infoLabelValue = formFieldConfig.infoLabel && !!formValue ? this.translate(formFieldConfig.infoLabel) : undefined;
    return [formValue, infoLabelValue].filter((s) => !!s).join(" ");
  }
}
